// SalesProjection.tsx
import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, useTheme, Chip, Alert } from '@mui/material';
import { TrendingUp, TrendingDown, Target, AlertTriangle, CheckCircle } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import {
  analyzePastEventsPattern,
  projectEventSales,
  getProjectionRecommendation
} from '../services/salesProjectionService';
import { PastEvent } from '../types/Event';

interface Registration {
  completed_at: string;
  tickets_count: number;
}

interface CurrentEvent {
  title: string;
  start_date: string;
  totalSold: number;
  registrations: Registration[];
  venueCapacity?: number;
  totalAvailable: number;
}

interface SalesProjectionProps {
  currentEvent: CurrentEvent;
  pastEvents: PastEvent[];
}

const SalesProjection: React.FC<SalesProjectionProps> = ({ currentEvent, pastEvents }) => {
  const theme = useTheme();

  // Analyze past events to get baseline pattern
  const baselinePattern = useMemo(() => {
    return analyzePastEventsPattern(pastEvents);
  }, [pastEvents]);

  // Project current event sales
  const projection = useMemo(() => {
    return projectEventSales(currentEvent, baselinePattern);
  }, [currentEvent, baselinePattern]);

  // Get recommendation
  const recommendation = useMemo(() => {
    return getProjectionRecommendation(projection);
  }, [projection]);

  // Prepare chart data
  const chartData = useMemo(() => {
    if (baselinePattern.length === 0) return [];

    const data = [];
    const capacity = currentEvent.venueCapacity || currentEvent.totalAvailable;

    // Add historical baseline pattern
    for (let week = 0; week <= projection.totalWeeks; week++) {
      const pattern = baselinePattern.find(p => p.weekNumber === week);
      const baselineValue = pattern 
        ? (pattern.cumulativePercentage / 100) * projection.projectedFinalSales
        : null;

      data.push({
        week: `Week ${week + 1}`,
        weekNumber: week,
        baseline: baselineValue ? Math.round(baselineValue) : null,
        projected: week <= projection.totalWeeks ? Math.round((week / projection.totalWeeks) * projection.projectedFinalSales) : null,
        capacity: capacity
      });
    }

    // Add current actual sales
    data[projection.weeksOnSale] = {
      ...data[projection.weeksOnSale],
      actual: currentEvent.totalSold
    };

    return data;
  }, [baselinePattern, projection, currentEvent]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            padding: '12px',
            borderRadius: '8px',
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 'bold', mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color, fontSize: '0.875rem' }}
            >
              {entry.name === 'baseline' && 'Typical Pattern: '}
              {entry.name === 'projected' && 'Projected: '}
              {entry.name === 'actual' && 'Current Actual: '}
              {entry.name === 'capacity' && 'Venue Capacity: '}
              {entry.value} tickets
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return theme.palette.success.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.error.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getStatusIcon = () => {
    if (projection.isOnTrack) {
      return <CheckCircle size={20} color={theme.palette.success.main} />;
    }
    if (projection.projectedCapacityPercentage >= 60) {
      return <AlertTriangle size={20} color={theme.palette.warning.main} />;
    }
    return <AlertTriangle size={20} color={theme.palette.error.main} />;
  };

  if (baselinePattern.length === 0) {
    return (
      <Card elevation={3} sx={{ mt: 3 }}>
        <CardContent>
          <Alert severity="info">
            Not enough historical data to generate sales projections. Need at least 3 past events with registration data.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      elevation={3}
      sx={{
        mt: 3,
        background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.8) 100%)'
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Target size={24} />
            Sales Projection
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${projection.confidence.toUpperCase()} Confidence`}
              size="small"
              sx={{
                backgroundColor: getConfidenceColor(projection.confidence),
                color: '#fff',
                fontWeight: 'bold'
              }}
            />
            {getStatusIcon()}
          </Box>
        </Box>

        {/* Key Metrics */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Current Sales</Typography>
            <Typography variant="h5">{projection.currentSold} tickets</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Projected Final</Typography>
            <Typography variant="h5" sx={{ color: theme.palette.primary.main }}>
              {projection.projectedFinalSales} tickets
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Projected Capacity</Typography>
            <Typography variant="h5" sx={{
              color: projection.projectedCapacityPercentage >= 80 
                ? theme.palette.success.main 
                : projection.projectedCapacityPercentage >= 60
                  ? theme.palette.warning.main
                  : theme.palette.error.main
            }}>
              {projection.projectedCapacityPercentage}%
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Days Until Event</Typography>
            <Typography variant="h5">{projection.daysUntilEvent} days</Typography>
          </Box>
        </Box>

        {/* Chart */}
        <Box sx={{ width: '100%', height: 350, mt: 3 }}>
          <ResponsiveContainer>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
              <defs>
                <linearGradient id="baselineGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#94a3b8" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#94a3b8" stopOpacity={0.2} />
                </linearGradient>
                <linearGradient id="projectedGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.8} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0.2} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
              <XAxis
                dataKey="week"
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.75rem' }}
              />
              <YAxis
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.75rem' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: '0.875rem' }} />
              
              {/* Capacity reference line */}
              <ReferenceLine
                y={currentEvent.venueCapacity || currentEvent.totalAvailable}
                stroke={theme.palette.success.light}
                strokeDasharray="5 5"
                label={{
                  value: 'Venue Capacity',
                  position: 'insideTopRight',
                  fill: theme.palette.success.light,
                  fontSize: 12
                }}
              />

              {/* Lines */}
              <Line
                type="monotone"
                dataKey="baseline"
                stroke="#94a3b8"
                strokeWidth={2}
                dot={false}
                name="Typical Pattern"
                strokeDasharray="5 5"
              />
              <Line
                type="monotone"
                dataKey="projected"
                stroke="#6366f1"
                strokeWidth={3}
                dot={false}
                name="Projected"
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#10b981"
                strokeWidth={4}
                dot={{ r: 6, fill: '#10b981', strokeWidth: 2, stroke: '#fff' }}
                name="Current Actual"
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        {/* Recommendation */}
        <Alert
          severity={
            projection.isOnTrack ? 'success' :
            projection.projectedCapacityPercentage >= 60 ? 'warning' : 'error'
          }
          icon={getStatusIcon()}
          sx={{ mt: 3 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {recommendation}
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
            Based on {pastEvents.length} historical events. Week {projection.weeksOnSale + 1} of {projection.totalWeeks}.
          </Typography>
        </Alert>
      </CardContent>
    </Card>
  );
};

export default SalesProjection;